import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { useIcp, useUpdateIcp } from '@/hooks/useIcp';
import { Button, Card, Input } from '@solid/ui';
import { RichMarkdownEditor } from '@/components/RichMarkdownEditor';

export default function IcpSettings() {
  const { data: icp, isLoading } = useIcp();
  const updateIcp = useUpdateIcp();

  const [name, setName] = useState('');
  const [markdownContent, setMarkdownContent] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (icp) {
      setName(icp.name);
      setMarkdownContent(icp.markdownContent || '');
      setDirty(false);
    }
  }, [icp]);

  const handleSave = async () => {
    if (!icp) return;
    await updateIcp.mutateAsync({
      id: icp.id,
      data: { name, markdownContent },
    });
    setDirty(false);
  };

  if (isLoading) {
    return <div className="text-grey-500">Loading...</div>;
  }

  if (!icp) {
    return <div className="text-grey-500">No ICP profile found.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-heading-2 text-secondary">Ideal Customer Profile</h2>
          <p className="text-body-sm text-grey-500 mt-1 max-w-2xl">
            Describe your ideal customer in plain language. This is delivered to the AI SDR agent verbatim and used to evaluate incoming leads.
          </p>
        </div>
        <Button onClick={handleSave} disabled={!dirty} loading={updateIcp.isPending}>
          {updateIcp.isPending ? (
            'Saving...'
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <Card padding="sm">
        <Input
          label="Profile Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setDirty(true);
          }}
          placeholder="e.g., Solid Digital Default ICP"
        />
      </Card>

      <Card padding="sm">
        <label className="block text-sm font-medium text-grey-700 mb-2">ICP Description</label>
        <RichMarkdownEditor
          value={markdownContent}
          onChange={(md) => {
            setMarkdownContent(md);
            setDirty(true);
          }}
          placeholder="Describe who your ideal customer is — size, industries, project types, budget, timeline, qualification signals..."
          minHeight="32rem"
        />
        <p className="text-xs text-grey-400 mt-2">
          Tip: use headings, lists, and bold to organize sections. The AI agent will read this exactly as written.
        </p>
      </Card>
    </div>
  );
}
